import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import { StatusCode } from "api/protocols";

import Page from "components/page/Page";
import Input from "components/form/input/Input";
import { Select } from "components/select/";
import FormLayout from "components/form/FormLayout";
import Card from "components/card/Card";
import Form from "components/form/Form";

import { isEqualWith, isNull } from "lodash";
import { showError } from "redux/actions/ui";
import { PostDivision } from "api/rpc/2024-04/facilityAdmin/facility/division";
import { GetCourse } from "api/rpc/2024-04/facilityAdmin/facility/course";
import { useAppDispatch } from "hooks/redux";



interface IDivision {
  title: string;
  course_id: number;
  location?: string;
}

export default function DivisionNew() {
  const { t } = useTranslation();
  const { Option } = Select;
  const history = useHistory();
  const dispatch = useAppDispatch();
  
  const [divisionState, setDivisionState] = useState<IDivision>({
    title: "",
    course_id: null,
    location: "front",
  });

  const [courseState, setCourseState] = useState({
    courses: [],
  });

  const [divisionStateBeforeChanges, setDivisionStateBeforeChanges] = useState<IDivision>(undefined);
  const [showLocationSelect, setShowLocationSelect] = useState(false);

  useEffect(() => {
    void loadCourses();
  }, []);

  useEffect(() => {
    void checkCourse();
  }, [divisionState.course_id]);

  function checkCourse() { 
    if (divisionState.course_id === null) {
      return;
    }

    const courseHas9or27Holes = courseState.courses.find(
      course => course.id === divisionState.course_id && (course.holes === 9 || course.holes === 27),
    );

    if (courseHas9or27Holes) {
      setDivisionState(prev => ({ ...prev, course_id: divisionState.course_id, location: "front" }));
      setShowLocationSelect(false);
    } else {
      setShowLocationSelect(true);
    }
  }

  async function loadCourses() {
    const courseRes = await GetCourse(undefined, true);
    if (courseRes.status !== StatusCode.OK) {
      dispatch(showError(t("Courses do not exist")));
      return;
    }
    setCourseState(prev => ({
      ...prev,
      courses: courseRes.data,
    }));
    setDivisionState(prev => ({...prev, course_id: courseRes.data[0].divisions[0].course_id}));
    setDivisionStateBeforeChanges(prev => ({...prev, course_id: courseRes.data[0].divisions[0].course_id}));
  }

  async function createDivision() {
    const postDivisionRes = await PostDivision(
      {
        title: divisionState.title.toLowerCase(),
        course_id: divisionState.course_id,
        location: divisionState.location.toLowerCase(),
      },
      true,
    );

    if (postDivisionRes.status !== StatusCode.OK) {
      dispatch(showError(t("Division")));
      return;
    }

    // Go back to page
    history.push("/admin/settings/division");
  }

  function handleInputChange(event: any) {
    const { id, value } = event.target;
    setDivisionState(prevState => ({ ...prevState, [id]: value }));
  }

  function handleDropDownChange(value: any, property: string) {
    setDivisionState(prev => ({ ...prev, [property]: value }));
  }

  function unsavedChangesExist() {
    if (divisionStateBeforeChanges === undefined) {
      if (divisionState) {
        setDivisionStateBeforeChanges(divisionState);
      }
      return false;
    }

    return !isEqualWith(divisionStateBeforeChanges, divisionState, (originalValue, newValue) => {
      if ((isNull(originalValue) || originalValue === "") && (isNull(newValue) || newValue === "")) {
        return true;
      }
    });
  }

  function cancelUnsavedChanges() {
    setDivisionState(divisionStateBeforeChanges);
  }

  const locationSelectOptions = [
    {
      title: t("Front"),
      id: "front",
    },
    {
      title: t("Back"),
      id: "back",
    },
  ];

  return (
    <Page
      title={t("Division")}
      narrow
      breadcrumbs={[
        {
          prefix: true,
          label: t("Divisions"),
          url: "/admin/settings/division",
        },
      ]}
      notificationBarProps={{
        isVisible: unsavedChangesExist(),
        onAction: createDivision,
        onCancel: cancelUnsavedChanges,
      }}
    >
      {divisionState && divisionStateBeforeChanges && (
        <Form>
          <Card>
            <Card.Section>
              <FormLayout>
                <FormLayout.Group>
                  <Input
                    value={divisionState.title}
                    label={t("Title")}
                    id="title"
                    onChange={handleInputChange}
                    placeholder={t("Title")}
                  />
                </FormLayout.Group>
                {courseState.courses.length > 1 ? (
                  <FormLayout.Group>
                    <Select
                      label={t("Course")}
                      onChange={(value: any) => handleDropDownChange(value, "course_id")}
                      defaultValue={divisionState.course_id}
                    >
                      {courseState.courses.map((course: any, index: number) => {
                        return (
                          <Option key={index} value={course.id} name={course.title}>
                            {course.long_name}
                          </Option>
                        );
                      })}
                    </Select>
                  </FormLayout.Group>
                ) : null}
                {showLocationSelect || (courseState?.courses?.length === 1 && courseState?.courses[0].holes === 18) ? (
                  <FormLayout.Group>
                    <Select
                      label={t("Location")}
                      onChange={(value: any) => handleDropDownChange(value, "location")}
                      defaultValue={divisionState.location}
                    >
                      {locationSelectOptions.map((location: any, index: number) => {
                        return (
                          <Option key={index} value={location.id} name={location.title}>
                            {location.title}
                          </Option>
                        );
                      })}
                    </Select>
                  </FormLayout.Group>
                ) : null}
              </FormLayout>
            </Card.Section>
          </Card>
        </Form>
      )}
    </Page>
  );
}
